import React, { Component } from 'react';
import T from 'components/common/translations/Translate';
import TransHelper from 'components/common/translations/TransHelper';
import { connect } from 'react-redux';
import HeaderBack from 'components/components/account/components/HeaderBack';
import withRouter from 'components/components/common/withRouter';
import { setUserLogout } from 'components/actions/authActions';
import {
  createSuccessNotification,
  createErrorNotification,
} from 'components/actions/serviceActions';
import isWindow from 'components/common/helpers/isWindow';
import ComplaintFormValidation from './utils/ComplaintFormValidation';
import {
  buildComplaintPayload,
  sendComplaint,
  parseComplaintResponse,
  parseComplaintError,
  emptyComplaintForm,
  isBotSubmission,
  HONEYPOT_HIDDEN_FIELD,
  HONEYPOT_CSS_FIELD,
  COMPLAINT_ERROR_INVALID_FORM,
  COMPLAINT_ERROR_INVALID_DATA,
  COMPLAINT_ERROR_SEND_FAILED,
} from './utils/complaintRequest';

const COMPLAINT_EMAIL = 'support@flyo.no';

class Complaint extends Component {

  state = {
    form: emptyComplaintForm(),
    errors: {},
    sending: false,
    // Set once Panel confirms the complaint was sent. `reference` is the case
    // number the user has to keep - it replaces the form on screen.
    sent: false,
    reference: '',
  }

  t = (key) => {
    return TransHelper.translate(this.props.locale, key);
  }

  trans = (key) => {
    return TransHelper.translate(this.props.locale, 'content.landing.complaint.' + key);
  }

  handleChange = (field) => (e) => {
    const value = e.target.value;
    this.setState((prev) => {
      // Drop the field's error as soon as the user starts correcting it.
      const errors = Object.assign({}, prev.errors);
      delete errors[field];
      return {
        form: Object.assign({}, prev.form, { [field]: value }),
        errors,
      };
    });
  }

  handleSubmit = (e) => {
    e.preventDefault();
    if (this.state.sending) return;

    const { form } = this.state;

    // Honeypots: bail out before doing any work, but report success so the bot
    // gets no signal about which field gave it away.
    if (isBotSubmission(form)) {
      this.setState({ form: emptyComplaintForm(), errors: {} });
      this.notifySuccess();
      return;
    }

    const validator = new ComplaintFormValidation(form, this.props.locale);
    if (!validator.isValid()) {
      this.setState({ errors: validator.getFieldErrors() });
      this.props.createErrorNotification({
        title: this.t('content.notifications.complaint.error'),
        message: this.t('content.notifications.complaint.requiredFields'),
      });
      return;
    }

    this.setState({ errors: {}, sending: true }, () => this.sendRequest());
  }

  sendRequest = () => {
    const payload = buildComplaintPayload(this.state.form, this.props.locale);

    sendComplaint(payload)
      .then((response) => {
        const { ok, reference } = parseComplaintResponse(response);
        if (!ok) {
          // 200 but not the contract we expect - do not claim it was sent.
          this.notifyFailure();
          return;
        }
        this.setState({
          form: emptyComplaintForm(),
          errors: {},
          sending: false,
          sent: true,
          reference: reference,
        }, this.scrollToTop);
        this.notifySuccess();
      })
      .catch((err) => {
        console.log('err', err);
        this.handleServerError(err);
      });
  }

  // Server-side validation. The form stays on screen with the data the user
  // typed - we only mark the fields Panel rejected and explain why.
  handleServerError = (err) => {
    const { code, fields } = parseComplaintError(err);

    if (fields.length) {
      const errors = {};
      fields.forEach((field) => {
        errors[field] = this.t('content.errors.complaint.' + field);
      });
      this.setState({ errors });
    }

    this.notifyFailure(this.serverErrorMessage(code, fields));
  }

  serverErrorMessage = (code, fields) => {
    switch (code) {
      case COMPLAINT_ERROR_INVALID_DATA:
        // Fields we could not map to an input would leave the user with a
        // highlighted-nothing form, so fall back to the generic wording.
        return fields.length
          ? this.t('content.notifications.complaint.invalidData')
          : this.t('content.notifications.complaint.invalidForm');
      case COMPLAINT_ERROR_INVALID_FORM:
        return this.t('content.notifications.complaint.invalidForm');
      case COMPLAINT_ERROR_SEND_FAILED:
        return this.t('content.notifications.complaint.sendFailed');
      default:
        return this.t('content.notifications.complaint.cannotBeSent');
    }
  }

  notifySuccess = () => {
    this.props.createSuccessNotification({
      title: this.t('content.notifications.complaint.success'),
      message: this.t('content.notifications.complaint.successMessage'),
    });
  }

  // The success card is much shorter than the form, so without this the user
  // is left staring at the footer. SSR-safe per the isWindow convention.
  scrollToTop = () => {
    if (!isWindow) return;
    window.scrollTo(0, 0);
  }

  notifyFailure = (message) => {
    this.setState({ sending: false });
    this.props.createErrorNotification({
      title: this.t('content.notifications.complaint.error'),
      message: message || this.t('content.notifications.complaint.cannotBeSent'),
    });
  }

  fieldError = (field) => {
    const message = this.state.errors[field];
    if (!message) return null;
    return <span className="cp-error">{message}</span>;
  }

  inputClass = (field) => {
    return this.state.errors[field] ? 'cp-input--error' : '';
  }

  // Shown in place of the form once Panel confirmed the complaint was sent.
  // The reference number is the one thing the user has to keep, so it gets a
  // permanent spot on the page - a toast would disappear before they note it.
  renderSuccess = () => {
    const { reference } = this.state;

    return (
      <div className="cp-success-card">
        <h2><T>content.landing.complaint.success.title</T></h2>
        <p><T>content.landing.complaint.success.registered</T></p>

        {reference &&
          <div className="cp-reference">
            <span className="cp-reference__label">
              <T>content.landing.complaint.success.referenceLabel</T>
            </span>
            <strong className="cp-reference__number">{reference}</strong>
          </div>
        }

        <p><T>content.landing.complaint.success.keepIt</T></p>

        <p className="cp-success-attachments">
          <T>content.landing.complaint.success.attachments</T>
          {' '}
          <a href={`mailto:${COMPLAINT_EMAIL}`}>{COMPLAINT_EMAIL}</a>
          {' '}
          <T>content.landing.complaint.success.attachmentsEnd</T>
        </p>
      </div>
    );
  }

  render() {
    const { isLogged } = this.props;
    const { form, sending, sent } = this.state;

    return (
      <div className="content content--lp content--lp-text complaint-page">
        {isLogged &&
          <HeaderBack
            locale={this.props.locale}
            name="content.layout.sidebarAndHeader.help"
            link="routes.url.help"
            title="content.layout.sidebarAndHeader.backToHelpTitle"
            className="header-back--lp" />
        }

        <div className="cp-container">

          {/* INTRO */}
          <div className="cp-intro-card">
            <h1><T>content.landing.complaint.title</T></h1>
            <p><T>content.landing.complaint.introFirst</T></p>
            <p><T>content.landing.complaint.introSecond</T></p>
          </div>

          {/* SUCCESS */}
          {sent && this.renderSuccess()}

          {/* FORM */}
          {!sent &&
          <div className="cp-form-card">
            <form onSubmit={this.handleSubmit} noValidate>

              {/* --- HONEYPOTS --------------------------------------------
                  Both must stay empty. Never remove the autoComplete/tabIndex
                  attributes - they keep browser autofill away from them. */}
              <input
                type="hidden"
                name={HONEYPOT_HIDDEN_FIELD}
                value={form[HONEYPOT_HIDDEN_FIELD]}
                onChange={this.handleChange(HONEYPOT_HIDDEN_FIELD)} />
              <div className="cp-hp" aria-hidden="true">
                <label htmlFor="complaint-homepage">Homepage</label>
                <input
                  type="text"
                  id="complaint-homepage"
                  name={HONEYPOT_CSS_FIELD}
                  value={form[HONEYPOT_CSS_FIELD]}
                  onChange={this.handleChange(HONEYPOT_CSS_FIELD)}
                  tabIndex="-1"
                  autoComplete="off" />
              </div>

              {/* 1. Contact details */}
              <div className="cp-section-title">
                <T>content.landing.complaint.form.contact.title</T>
              </div>
              <div className="cp-form-grid">
                <div className="cp-form-group">
                  <label htmlFor="complaint-name">
                    <T>content.landing.complaint.form.contact.name.label</T>
                    <span className="cp-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="complaint-name"
                    name="name"
                    className={this.inputClass('name')}
                    value={form.name}
                    onChange={this.handleChange('name')}
                    placeholder={this.trans('form.contact.name.placeholder')} />
                  {this.fieldError('name')}
                </div>
                <div className="cp-form-group">
                  <label htmlFor="complaint-email">
                    <T>content.landing.complaint.form.contact.email.label</T>
                    <span className="cp-required">*</span>
                  </label>
                  <input
                    type="email"
                    id="complaint-email"
                    name="email"
                    className={this.inputClass('email')}
                    value={form.email}
                    onChange={this.handleChange('email')}
                    placeholder={this.trans('form.contact.email.placeholder')} />
                  {this.fieldError('email')}
                </div>
                <div className="cp-form-group">
                  <label htmlFor="complaint-phone">
                    <T>content.landing.complaint.form.contact.phone.label</T>
                    <span className="cp-required">*</span>
                  </label>
                  <input
                    type="tel"
                    id="complaint-phone"
                    name="phone"
                    className={this.inputClass('phone')}
                    value={form.phone}
                    onChange={this.handleChange('phone')}
                    placeholder={this.trans('form.contact.phone.placeholder')} />
                  {this.fieldError('phone')}
                </div>
              </div>

              {/* 2. Trip details */}
              <div className="cp-section-title cp-section-title--spaced">
                <T>content.landing.complaint.form.trip.title</T>
              </div>
              <div className="cp-form-grid">
                <div className="cp-form-group">
                  <label htmlFor="complaint-datetime">
                    <T>content.landing.complaint.form.trip.datetime.label</T>
                    <span className="cp-required">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    id="complaint-datetime"
                    name="datetime"
                    className={this.inputClass('datetime')}
                    value={form.datetime}
                    onChange={this.handleChange('datetime')} />
                  {this.fieldError('datetime')}
                </div>
                <div className="cp-form-group">
                  <label htmlFor="complaint-license">
                    <T>content.landing.complaint.form.trip.license.label</T>
                  </label>
                  <input
                    type="text"
                    id="complaint-license"
                    name="license"
                    value={form.license}
                    onChange={this.handleChange('license')}
                    placeholder={this.trans('form.trip.license.placeholder')} />
                </div>
                <div className="cp-form-group">
                  <label htmlFor="complaint-pickup">
                    <T>content.landing.complaint.form.trip.pickup.label</T>
                    <span className="cp-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="complaint-pickup"
                    name="pickup"
                    className={this.inputClass('pickup')}
                    value={form.pickup}
                    onChange={this.handleChange('pickup')}
                    placeholder={this.trans('form.trip.pickup.placeholder')} />
                  {this.fieldError('pickup')}
                </div>
                <div className="cp-form-group">
                  <label htmlFor="complaint-dropoff">
                    <T>content.landing.complaint.form.trip.dropoff.label</T>
                    <span className="cp-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="complaint-dropoff"
                    name="dropoff"
                    className={this.inputClass('dropoff')}
                    value={form.dropoff}
                    onChange={this.handleChange('dropoff')}
                    placeholder={this.trans('form.trip.dropoff.placeholder')} />
                  {this.fieldError('dropoff')}
                </div>
              </div>

              {/* 3. The complaint itself */}
              <div className="cp-section-title cp-section-title--spaced">
                <T>content.landing.complaint.form.details.title</T>
              </div>
              <div className="cp-form-group">
                <label htmlFor="complaint-category">
                  <T>content.landing.complaint.form.details.category.label</T>
                  <span className="cp-required">*</span>
                </label>
                <select
                  id="complaint-category"
                  name="category"
                  className={this.inputClass('category')}
                  value={form.category}
                  onChange={this.handleChange('category')}>
                  <option value="">{this.trans('form.details.category.empty')}</option>
                  <option value="price">{this.trans('form.details.category.price')}</option>
                  <option value="delay">{this.trans('form.details.category.delay')}</option>
                  <option value="driver">{this.trans('form.details.category.driver')}</option>
                  <option value="route">{this.trans('form.details.category.route')}</option>
                  <option value="vehicle">{this.trans('form.details.category.vehicle')}</option>
                  <option value="other">{this.trans('form.details.category.other')}</option>
                </select>
                {this.fieldError('category')}
              </div>
              <div className="cp-form-group">
                <label htmlFor="complaint-description">
                  <T>content.landing.complaint.form.details.description.label</T>
                  <span className="cp-required">*</span>
                </label>
                <textarea
                  id="complaint-description"
                  name="description"
                  className={this.inputClass('description')}
                  value={form.description}
                  onChange={this.handleChange('description')}
                  placeholder={this.trans('form.details.description.placeholder')} />
                {this.fieldError('description')}
              </div>

              <div className="cp-form-group">
                <label><T>content.landing.complaint.form.details.attachments.label</T></label>
                <div className="cp-file-upload-box">
                  <p>
                    <T>content.landing.complaint.form.details.attachments.description</T>
                    {' '}
                    <a href={`mailto:${COMPLAINT_EMAIL}`}>{COMPLAINT_EMAIL}</a>
                    {' '}
                    <T>content.landing.complaint.form.details.attachments.descriptionEnd</T>
                  </p>
                </div>
              </div>

              <div className="cp-submit-wrapper">
                <button type="submit" className="cp-btn-submit" disabled={sending}>
                  {sending
                    ? <T>content.landing.complaint.form.sending</T>
                    : <T>content.landing.complaint.form.submit</T>
                  }
                </button>
              </div>
            </form>
          </div>
          }

          {/* INDEPENDENT COMPLAINTS BODY */}
          <div className="cp-info-box">
            <h3><T>content.landing.complaint.appeal.title</T></h3>
            <p>
              <T>content.landing.complaint.appeal.description</T>
              {' '}
              <a
                href="https://www.forbrukertilsynet.no"
                target="_blank"
                rel="noopener noreferrer">
                www.forbrukertilsynet.no
              </a>.
            </p>
          </div>

          {/* LEGAL NOTE */}
          <div className="cp-legal-note">
            <div>
              <strong>Flyo AS</strong> | <T>content.landing.complaint.legal.licenceHolder</T>
              <br />
              <T>content.landing.complaint.legal.email</T>
              {' '}
              <a href={`mailto:${COMPLAINT_EMAIL}`}>{COMPLAINT_EMAIL}</a>
            </div>
            <div>
              <T>content.landing.complaint.legal.compliance</T>
            </div>
          </div>

        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    locale: state.app.locale,
    isLogged: state.app.isLogged
  };
};

export default withRouter(
  connect(
    mapStateToProps,
    { setUserLogout, createSuccessNotification, createErrorNotification }
  )(Complaint));
